/**
 * Google Consent Mode v2
 *
 * GTM still loads for every visitor, but analytics/advertising storage starts
 * out denied in GDPR countries, so no `_ga` or ad cookies are written before
 * the visitor answers the cookie banner. Tags run in cookieless mode until
 * then, instead of not running at all.
 *
 * Consent Mode does the geo check itself via its `region` parameter, which is
 * what keeps this synchronous: the defaults have to reach the dataLayer before
 * the container loads, and the GeoIP lookup the banner uses is async.
 */
import { GDPR_COUNTRIES } from '@link/share/constants';

type ConsentValue = 'granted' | 'denied';

/** The banner cookie types that map onto a Consent Mode signal. */
export type ConsentCookieType = 'analytics' | 'advertising';

interface ConsentParams {
  ad_storage?: ConsentValue;
  ad_user_data?: ConsentValue;
  ad_personalization?: ConsentValue;
  analytics_storage?: ConsentValue;
  region?: readonly string[];
  wait_for_update?: number;
}

type Gtag = (
  command: 'consent',
  arg: 'default' | 'update',
  params: ConsentParams
) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** How long tags wait for the banner's answer before firing denied. */
const CONSENT_UPDATE_TIMEOUT = 500;

/** Where the Silktide banner keeps each cookie type's answer. */
const CONSENT_STORAGE_PREFIX = 'silktideCookieChoice_';

/**
 * The canonical gtag stub. GTM only recognizes a gtag command when the raw
 * `arguments` object reaches the dataLayer, so it must not be spread into an
 * array first.
 */
const gtag = function () {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
} as Gtag;

const consentParams = (
  cookieType: ConsentCookieType,
  value: ConsentValue
): ConsentParams =>
  cookieType === 'analytics'
    ? { analytics_storage: value }
    : {
        ad_storage: value,
        ad_user_data: value,
        ad_personalization: value
      };

/** The visitor's stored answer for one cookie type, or null if never asked. */
const readStoredConsent = (cookieType: ConsentCookieType): boolean | null => {
  const stored = localStorage.getItem(`${CONSENT_STORAGE_PREFIX}${cookieType}`);
  return stored === null ? null : stored === 'true';
};

/** Push one cookie type's answer to Consent Mode. */
export function updateConsent(
  cookieType: ConsentCookieType,
  granted: boolean
): void {
  gtag(
    'consent',
    'update',
    consentParams(cookieType, granted ? 'granted' : 'denied')
  );
}

/**
 * Declare consent defaults. Must run before the GTM container loads —
 * `initGtm` calls it, since ES module imports are hoisted and app entrypoints
 * therefore cannot order this themselves.
 */
export function initConsentMode(): void {
  // Denied in GDPR countries, granted everywhere else. The most specific
  // `region` wins, so these two calls are order-independent.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: GDPR_COUNTRIES,
    wait_for_update: CONSENT_UPDATE_TIMEOUT
  });
  gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted'
  });

  // Replay a returning visitor's answer now, so their tags are not held for
  // `wait_for_update` on every page load while the banner script loads.
  (['analytics', 'advertising'] as const).forEach(cookieType => {
    const stored = readStoredConsent(cookieType);
    if (stored !== null) {
      updateConsent(cookieType, stored);
    }
  });
}
